import React, { FC, useState, useContext, useEffect } from 'react';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import OfflineWarningAlert from './OfflineWarningAlert';

import { backflowJobItemRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';

import { BackflowJobItem } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

interface BeginTestButtonProps {
  backflowJobItem: BackflowJobItem;
  onSuccess: () => void;
}

const BeginTestButton: FC<BeginTestButtonProps> = ({ backflowJobItem, onSuccess }) => {
  const { user } = useContext(AuthenticationContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string>('');
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // Monitor online/offline status
  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  const updateStatusInCache = (markAsNeedsSync: boolean = true): boolean => {
    try {
      const jobGroupId = backflowJobItem?.backflow_job_group_id;
      if (!jobGroupId) return false;

      const cacheKey = `backflowJobGroup_${jobGroupId}`;
      const cachedData = localStorage.getItem(cacheKey);

      if (!cachedData) {
        setErrorMessage('Job group not cached. Please visit the job group page while online first.');
        return false;
      }

      const jobGroupData = JSON.parse(cachedData);
      const itemIndex = jobGroupData.jobItems?.findIndex((item: any) => item.id === backflowJobItem.id);

      if (itemIndex >= 0) {
        // Update status in backflow_job_item sub-object
        if (jobGroupData.jobItems[itemIndex].backflow_job_item) {
          jobGroupData.jobItems[itemIndex].backflow_job_item.status = 'in_progress';
          jobGroupData.jobItems[itemIndex].backflow_job_item.emp_id = user.id.toString();
          jobGroupData.jobItems[itemIndex].backflow_job_item.needsSync = markAsNeedsSync;
          jobGroupData.jobItems[itemIndex].backflow_job_item.savedAt = new Date().toISOString();
        }
        // backflow_log remains unchanged

        localStorage.setItem(cacheKey, JSON.stringify(jobGroupData));
        return true;
      } else {
        setErrorMessage('Job item not found in cache.');
        return false;
      }
    } catch (error) {
      console.error('Error updating status in cache:', error);
      setErrorMessage('Failed to update status in cache.');
      return false;
    }
  };

  const handleBeginTest = async () => {
    const validate = validateRequiredFields(backflowJobItem, backflowJobItemRequiredFields);
    setValidationErrors(validate.message);
    if (!validate.isValid) return;

    setLoading(true);

    // If offline, update cache only
    if (!isOnline) {
      const success = updateStatusInCache(true); // Mark as needs sync
      if (success) {
        setErrorMessage('');
        onSuccess();
      }
      setLoading(false);
      return;
    }

    // If online, update server
    try {
      const jobItemUpdateData = { user_id: user.id.toString(), status: 'in_progress' };

      await axios.put(`/backflows/backflow_job_items/${backflowJobItem.id}`, jobItemUpdateData);

      // Also update cache (mark as synced since server succeeded)
      updateStatusInCache(false);

      setErrorMessage('');
      onSuccess();
    } catch (error: any) {
      const isNetworkError = !navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error';

      if (isNetworkError) {
        // Connection lost during save - fallback to cache (mark as needs sync)
        const success = updateStatusInCache(true);
        if (success) {
          setErrorMessage('');
          onSuccess();
        }
      } else {
        setErrorMessage('Something went wrong beginning the test.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Grid container>
        <OfflineWarningAlert isOnline={isOnline} />
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage || validationErrors} />
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
          {user?.id ? (
            <Button variant="contained" onClick={handleBeginTest}>
              Begin Test
            </Button>
          ) : (
            <Typography variant="body1">You must be logged in to begin a test.</Typography>
          )}
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default BeginTestButton;
